
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  BookUser,
  Play,
  Clock,
  Trophy,
  Star,
  CheckCircle,
  Users,
  MessageCircle,
  Brain
} from 'lucide-react';

const StaffTraining = () => {
  const [activeModule, setActiveModule] = useState(null);

  const trainingModules = [
    {
      id: 1,
      title: "Patient Communication Excellence",
      description: "Master the art of patient interactions and consultation skills",
      duration: "45 min",
      completed: true,
      progress: 100,
      instructor: "Alma AI",
      category: "Communication"
    },
    {
      id: 2,
      title: "JUV Credit System Mastery",
      description: "Learn to optimize credit usage and member satisfaction",
      duration: "30 min",
      completed: true,
      progress: 100,
      instructor: "Alma AI",
      category: "Operations"
    },
    {
      id: 3,
      title: "Treatment Consultation & Booking",
      description: "Guide patients through treatment selection and booking process",
      duration: "60 min",
      completed: false,
      progress: 65,
      instructor: "Alma AI",
      category: "Sales"
    },
    {
      id: 4,
      title: "Handling Difficult Situations",
      description: "De-escalation techniques and problem resolution",
      duration: "40 min",
      completed: false,
      progress: 0,
      instructor: "Alma AI",
      category: "Communication"
    }
  ];

  const achievements = [
    { title: "First Week Complete", icon: Trophy, earned: true },
    { title: "Communication Expert", icon: MessageCircle, earned: true },
    { title: "Credit Specialist", icon: Star, earned: true },
    { title: "Patient Advocate", icon: Users, earned: false }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-800">
                Staff Training Hub
              </h1>
              <p className="mt-1 text-slate-600">
                Powered by Alma AI - Your Personal Training Assistant
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className="bg-blue-100 text-blue-800">
                <Brain className="w-4 h-4 mr-1" />
                AI-Powered Learning
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar - Progress Overview */}
          <div className="lg:col-span-1 space-y-6">
            {/* Overall Progress */}
            <Card className="bg-white shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-800 flex items-center">
                  <BookUser className="w-5 h-5 mr-2 text-blue-600" />
                  Your Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Overall Completion</span>
                      <span className="font-medium text-slate-800">66%</span>
                    </div>
                    <Progress value={66} className="h-2" />
                  </div>
                  
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-600 mb-2">Modules Completed</p>
                    <p className="text-2xl font-bold text-slate-800">2 of 4</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-600 mb-2">Time Invested</p>
                    <p className="text-xl font-semibold text-blue-600">1h 15m</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Achievements */}
            <Card className="bg-white shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-800">Achievements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {achievements.map((achievement, index) => {
                    const Icon = achievement.icon;
                    return (
                      <div key={index} className={`flex items-center space-x-3 p-2 rounded-lg ${
                        achievement.earned ? 'bg-green-50' : 'bg-slate-50'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                          achievement.earned ? 'text-green-600' : 'text-slate-400'
                        }`} />
                        <span className={`text-sm ${
                          achievement.earned ? 'text-green-800 font-medium' : 'text-slate-500'
                        }`}>
                          {achievement.title}
                        </span>
                        {achievement.earned && (
                          <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Training Modules */}
          <div className="lg:col-span-3">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-slate-800">Training Modules</h2>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat with Alma AI
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {trainingModules.map((module) => (
                  <Card key={module.id} className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg text-slate-800 mb-2">
                            {module.title}
                          </CardTitle>
                          <p className="text-sm text-slate-600 mb-3">
                            {module.description}
                          </p>
                        </div>
                        {module.completed && (
                          <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-slate-500">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {module.duration}
                        </div>
                        <div className="flex items-center">
                          <Brain className="w-4 h-4 mr-1" />
                          {module.instructor}
                        </div>
                      </div>
                      
                      <Badge variant="outline" className="w-fit">
                        {module.category}
                      </Badge>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-4">
                        {/* Progress Bar */}
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-600">Progress</span>
                            <span className="font-medium text-slate-800">{module.progress}%</span>
                          </div>
                          <Progress value={module.progress} className="h-2" />
                        </div>
                        
                        {/* Action Button */}
                        <Button 
                          className={`w-full ${
                            module.completed 
                              ? 'bg-green-600 hover:bg-green-700' 
                              : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                          onClick={() => setActiveModule(module.id)}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {module.completed ? 'Review' : module.progress > 0 ? 'Continue' : 'Start'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Quick Actions */}
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                      <MessageCircle className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-800">
                      Need Help? Chat with Alma AI
                    </h3>
                    <p className="text-slate-600 max-w-md mx-auto">
                      Our AI training assistant is available 24/7 to answer questions, 
                      provide guidance, and help you master your skills.
                    </p>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      Start Conversation
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffTraining;
